import { Calendar, Eye, Clock, Edit, Trash2, CheckCircle, XCircle, BookOpen, User, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { cleanAuthorName, getAuthorInitial } from '@/lib/utils';
import type { Blog } from '@/lib/api/types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

interface BlogCardProps {
  blog: Blog;
  onEdit: () => void;
  onDelete: (blog: Blog) => void;
  onTogglePublish: (blogId: string, currentStatus: string) => void;
}

export function BlogCard({ blog, onEdit, onDelete, onTogglePublish }: BlogCardProps) {
  const formattedDate = blog.publishedAt
    ? format(new Date(blog.publishedAt), 'MMM dd, yyyy')
    : format(new Date(blog.createdAt), 'MMM dd, yyyy');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden h-full flex flex-col group hover:shadow-md transition-all duration-200 border-border/50">
        {/* Cover Image */}
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
          {blog.coverImageUrl ? (
            <img
              src={blog.coverImageUrl}
              alt={blog.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <BookOpen className="w-24 h-24 opacity-20" />
            </div>
          )}

          {/* Status Badge */}
          <div className="absolute top-3 right-3">
            <Badge
              variant={blog.status === 'published' ? 'default' : 'secondary'}
              className={`text-xs font-medium ${blog.status === 'published'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
            >
              {blog.status === 'published' ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Published
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Draft
                </span>
              )}
            </Badge>
          </div>
        </div>

        <CardContent className="p-5 flex-1 flex flex-col">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {blog.title}
          </h3>

          {/* Excerpt */}
          <p className="text-sm text-muted-foreground mb-4 line-clamp-3 flex-1">
            {blog.excerpt}
          </p>

          {/* Meta Info */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {blog.readingTimeMinutes} min
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {blog.viewCount} views
            </span>
          </div>

          {/* Author */}
          <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-medium">
              {getAuthorInitial(blog.authorName)}
            </div>
            <span className="text-sm font-medium">{cleanAuthorName(blog.authorName)}</span>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {blog.status === 'published' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const blogUrl = blog.slug
                    ? `https://elimishacharity.org/blog/${blog.slug}`
                    : `https://elimishacharity.org/blog/${blog.id}`;
                  window.open(blogUrl, '_blank');
                }}
                className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <PermissionGuard resource="blogs" action="publish">
              <Button
                variant={blog.status === 'published' ? 'outline' : 'default'}
                size="sm"
                onClick={() => onTogglePublish(blog.id, blog.status)}
                className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0"
              >
                {blog.status === 'published' ? 'Unpublish' : 'Publish'}
              </Button>
            </PermissionGuard>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(blog)}
              className="hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors min-w-[calc(50%-0.25rem)] sm:min-w-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
